import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  useToast,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import { createMovement } from 'services/currentAccount.service';
import httpClient from 'utils/httpClient';

export default function CreateMovementModal({ isOpen, onClose, account, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formData, setFormData] = useState({
    movementType: 'CREDIT',
    amount: '',
    description: '',
    movementDate: new Date().toISOString().split('T')[0],
    paymentMethodId: '',
    referenceType: 'PAYMENT',
  });

  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadPaymentMethods();
    }
  }, [isOpen]);

  const loadPaymentMethods = async () => {
    try {
      const response = await httpClient.get('/payment-methods');
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.payload)
          ? response.payload
          : Array.isArray(response?.data)
            ? response.data
            : [];
      setPaymentMethods(list);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setPaymentMethods([]);
      toast({
        title: 'No se pudieron cargar los métodos de pago',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    const amount = parseFloat(formData.amount);
    if (formData.amount === '' || Number.isNaN(amount) || amount < 0) {
      toast({
        title: 'Error',
        description: 'Ingrese un monto válido (0 o mayor)',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.description) {
      toast({
        title: 'Error',
        description: 'La descripción es obligatoria',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.movementType === 'CREDIT' && amount > 0 && !formData.paymentMethodId) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar un método de pago para créditos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        accountId: account.accountId,
        movementType: formData.movementType,
        amount,
        description: formData.description,
        movementDate: formData.movementDate,
        referenceType: formData.referenceType,
        paymentMethodId: formData.paymentMethodId ? parseInt(formData.paymentMethodId) : null,
        createdBy: 1, // TODO: obtener del usuario logueado
      };

      const response = await createMovement(payload);

      if (response.status === 201) {
        toast({
          title: 'Éxito',
          description: 'Movimiento registrado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo registrar el movimiento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Nuevo Movimiento - {account?.studentName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {/* Tipo de movimiento */}
            <FormControl isRequired>
              <FormLabel>Tipo de Movimiento</FormLabel>
              <RadioGroup
                value={formData.movementType}
                onChange={(value) => handleChange('movementType', value)}
              >
                <Stack direction="row" spacing={4}>
                  <Radio value="CREDIT" colorScheme="green">
                    Crédito (Pago)
                  </Radio>
                  <Radio value="DEBIT" colorScheme="red">
                    Débito (Cargo)
                  </Radio>
                </Stack>
              </RadioGroup>
              <Text fontSize="xs" color="gray.500" mt={2}>
                {formData.movementType === 'CREDIT'
                  ? 'El crédito disminuye la deuda del estudiante'
                  : 'El débito aumenta la deuda del estudiante'}
              </Text>
            </FormControl>

            {/* Monto */}
            <FormControl isRequired>
              <FormLabel>Monto</FormLabel>
              <NumberInput
                min={0}
                value={formData.amount}
                onChange={(value) => handleChange('amount', value)}
              >
                <NumberInputField placeholder="Ingrese el monto" />
              </NumberInput>
            </FormControl>

            {/* Fecha */}
            <FormControl isRequired>
              <FormLabel>Fecha del Movimiento</FormLabel>
              <Input
                type="date"
                value={formData.movementDate}
                onChange={(e) => handleChange('movementDate', e.target.value)}
              />
            </FormControl>

            {/* Método de pago (solo para créditos) */}
            {formData.movementType === 'CREDIT' && parseFloat(formData.amount || 0) > 0 && (
              <FormControl isRequired>
                <FormLabel>Método de Pago</FormLabel>
                <Select
                  placeholder="Seleccione método de pago"
                  value={formData.paymentMethodId}
                  onChange={(e) => handleChange('paymentMethodId', e.target.value)}
                >
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.description}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Tipo de referencia */}
            <FormControl>
              <FormLabel>Tipo de Referencia</FormLabel>
              <Select
                value={formData.referenceType}
                onChange={(e) => handleChange('referenceType', e.target.value)}
              >
                <option value="PAYMENT">Pago</option>
                <option value="INSCRIPTION">Inscripción</option>
                <option value="EXAM">Examen</option>
                <option value="LIBRARY">Biblioteca</option>
                <option value="OTHER">Otro</option>
              </Select>
            </FormControl>

            {/* Descripción */}
            <FormControl isRequired>
              <FormLabel>Descripción</FormLabel>
              <Textarea
                placeholder="Ingrese una descripción detallada"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
              />
            </FormControl>

            {/* Preview del saldo */}
            <FormControl>
              <FormLabel>Saldo Actual</FormLabel>
              <Text fontSize="lg" fontWeight="bold" color={account?.balance > 0 ? 'red.500' : 'green.500'}>
                {new Intl.NumberFormat('es-UY', {
                  style: 'currency',
                  currency: 'UYU',
                }).format(account?.balance || 0)}
              </Text>
              {formData.amount !== '' && !Number.isNaN(parseFloat(formData.amount)) && (
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Nuevo saldo:{' '}
                  {new Intl.NumberFormat('es-UY', {
                    style: 'currency',
                    currency: 'UYU',
                  }).format(
                    formData.movementType === 'DEBIT'
                      ? (account?.balance || 0) + parseFloat(formData.amount)
                      : (account?.balance || 0) - parseFloat(formData.amount)
                  )}
                </Text>
              )}
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancelar
          </Button>
          <Button
            colorScheme={formData.movementType === 'DEBIT' ? 'red' : 'green'}
            onClick={handleSubmit}
          >
            {formData.movementType === 'DEBIT' ? 'Registrar Cargo' : 'Registrar Pago'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
